import * as v from 'valibot';

/**
 * The filesystem a run owns.
 *
 * Container steps used to get a sandbox each, with their state replayed into
 * it — which is right for "run this code" and wrong for anything that spans
 * steps. A workflow whose third step implements a change and whose fourth opens
 * a pull request needs the two to be looking at the same checkout, and before
 * this there was no such thing.
 *
 * Snapshots are what make it fit reef rather than fight it. Forking a run from
 * step 4 already promises the conversation as step 4 left it; a snapshot per
 * step boundary extends the same promise to the disk.
 */
export const WorkspaceSnapshot = v.object({
  id: v.string(),
  runId: v.string(),
  /** Null for the snapshot taken when the workspace was first materialized. */
  stepId: v.nullable(v.string()),
  byteSize: v.pipe(v.number(), v.integer()),
  fileCount: v.pipe(v.number(), v.integer()),
  /** Content digest, so an unchanged step does not store a second identical tar. */
  digest: v.nullable(v.string()),
  createdAt: v.string(),
});
export type WorkspaceSnapshot = v.InferOutput<typeof WorkspaceSnapshot>;

/**
 * A file in a workspace, as it crosses the bridge.
 *
 * Text only, deliberately. A workspace that can carry arbitrary binaries is a
 * workspace that can carry an exfiltrated database dump out through the
 * snapshot path, and every use reef actually has for this — source, configs,
 * patches, reports — is text. Binary belongs in an artifact, where it is
 * addressed, versioned and visible.
 */
export const WorkspaceFile = v.object({
  path: v.pipe(v.string(), v.minLength(1), v.maxLength(1024)),
  content: v.string(),
});
export type WorkspaceFile = v.InferOutput<typeof WorkspaceFile>;

/** Ceilings per run, so a runaway loop cannot fill the bucket. */
export const MAX_WORKSPACE_BYTES = 64 * 1024 * 1024;
export const MAX_WORKSPACE_FILES = 5_000;

/**
 * Paths a workspace refuses.
 *
 * `..` is the obvious one. The rest are absolute paths and Windows drive
 * letters, which a model producing a path from a repository URL will generate
 * eventually — and a workspace write that escapes its own directory is the
 * whole security story of this feature.
 */
export function isSafeWorkspacePath(path: string): boolean {
  if (path.length === 0 || path.length > 1024) return false;
  if (path.startsWith('/') || /^[A-Za-z]:/.test(path)) return false;
  if (path.split('/').some((segment) => segment === '..' || segment === '')) return false;
  // Control characters: no real path has one, and they break the tar. Compared
  // by code point rather than by a regex class, so the check itself contains no
  // unprintable bytes for a reviewer to squint at.
  return ![...path].some((character) => character.codePointAt(0)! < 0x20);
}

/**
 * Repositories a step stands in.
 *
 * The workspace above is a run-scoped disk, and until this it was populated by
 * exactly one thing: a warm lease for a single `(repo, ref)`, read off whatever
 * codebase-index mount the step happened to carry. That is enough for a step
 * that reads one repository and nothing else. It has nowhere to put a change
 * that spans two — the sentence "move the shared type out of `acme/web` into
 * `acme/contracts` and update both" describes work reef could not lay out on a
 * disk, let alone commit.
 *
 * So a step says which repositories it works in, and the runtime checks each
 * one out under its own `path` before the model is asked to do anything.
 */

/**
 * How many repositories one step may declare.
 *
 * Ten, and the number is deliberately generous rather than tight, because it is
 * not the ceiling that bites. A workspace holds `MAX_WORKSPACE_FILES` files and
 * `MAX_WORKSPACE_BYTES` bytes across *all* of its checkouts, and a single clone
 * refuses past two thousand files — so three ordinary repositories already fill
 * the disk and the fourth is refused by the ceiling that was there before this
 * field existed. What ten is for is the other failure: `repos` built from a
 * list a previous step discovered, mistyped into forty entries, spending forty
 * repositories' worth of GitHub round trips and reach checks before the disk
 * ceiling gets a chance to say no. Ten is refused at parse time, in the diff,
 * for nothing.
 */
export const MAX_WORKSPACE_REPOS = 10;

/**
 * `owner/repo`, and nothing else.
 *
 * A URL would parse and then fail at clone: reef's git server is GitHub-only
 * and mints an installation token per *owner*, so `git@gitlab.com:...` is a
 * value the schema would accept and the runtime could never satisfy. A field
 * that only fails at three in the morning is worse than a field that fails in
 * review.
 */
const REMOTE_PATTERN = /^[A-Za-z0-9][A-Za-z0-9._-]*\/[A-Za-z0-9][A-Za-z0-9._-]*$/;

export const WorkspaceRepoRef = v.object({
  /** `owner/repo` on GitHub. */
  remote: v.pipe(v.string(), v.regex(REMOTE_PATTERN, 'must be "owner/repo"')),
  /**
   * The branch, tag or commit to check out. Absent means the repository's
   * default branch, resolved at clone time rather than written down — a
   * document that hard-coded `main` would be wrong for every repository that
   * renamed it.
   */
  branch: v.optional(v.pipe(v.string(), v.minLength(1), v.maxLength(255))),
  /**
   * Where in the workspace the tree goes. Absent is the workspace root, which
   * only one repository in a set may use — see `workspaceRepoIssues`.
   */
  path: v.optional(v.pipe(v.string(), v.minLength(1), v.maxLength(255))),
});
export type WorkspaceRepoRef = v.InferOutput<typeof WorkspaceRepoRef>;

export const WorkspaceSpec = v.object({
  repos: v.pipe(v.array(WorkspaceRepoRef), v.maxLength(MAX_WORKSPACE_REPOS)),
  /**
   * A named repo set and the secrets scope that goes with it, resolved from D1.
   *
   * Not a second way to write `repos`: an environment is the thing an admin
   * configures once — "staging is these three repositories and these six
   * secrets" — so that a workflow document names it rather than restating it,
   * and so that rotating a secret is not an edit to every workflow that reads
   * it. `packages/connectors/src/secrets.ts` already scopes secrets by
   * `environment`; this is the field that says which one.
   */
  environment: v.optional(v.pipe(v.string(), v.minLength(1), v.maxLength(120))),
});
export type WorkspaceSpec = v.InferOutput<typeof WorkspaceSpec>;

/**
 * A named repo set plus its secrets scope, as an admin configures it.
 *
 * Named `WorkspaceEnvironment` rather than `Environment` because reef already
 * has a `WarmEnvironment`, and the two words mean different things: a warm
 * environment is a *machine* held ready for one `(repo, ref)`, and this is a
 * *configuration* — which repositories, on which branches, with which secrets.
 * A step may name this and lease one of those in the same breath, so leaving
 * the collision unnamed would guarantee somebody eventually reads one comment
 * about the other.
 */
export const WorkspaceEnvironment = v.object({
  /** Stable id, and the `scopeId` a secret at `environment` scope is filed under. */
  id: v.pipe(v.string(), v.minLength(1), v.maxLength(120)),
  name: v.pipe(v.string(), v.minLength(1)),
  description: v.optional(v.nullable(v.string()), null),
  repos: v.pipe(v.array(WorkspaceRepoRef), v.maxLength(MAX_WORKSPACE_REPOS)),
  createdAt: v.optional(v.string()),
  updatedAt: v.optional(v.string()),
});
export type WorkspaceEnvironment = v.InferOutput<typeof WorkspaceEnvironment>;

/** A step's repositories after inheritance, with nothing left implicit but the default branch. */
export interface ResolvedWorkspace {
  repos: WorkspaceRepoRef[];
  environment: string | null;
}

/**
 * What is wrong with a repo set, in the author's own words.
 *
 * Three of these are the same failure wearing different hats, and it is worth
 * saying plainly because it is not obvious from the field: `git.clone` clears
 * everything under its own prefix that the new checkout does not contain, so
 * that re-cloning after a step mangled the tree does not leave the previous
 * checkout's deleted files lying around. A repository at the workspace root has
 * the empty prefix, which is *every* path — so a second repository under
 * `contracts/` is deleted the moment the root one is refreshed. Nested paths
 * (`web` and `web/api`) are the same collision one level down, and two
 * repositories at one path is it stated outright.
 *
 * All three parse. All three produce a step whose disk silently loses a
 * checkout it was told it had, which surfaces two steps later as a pull request
 * that deletes a repository.
 */
export function workspaceRepoIssues(repos: readonly WorkspaceRepoRef[]): string[] {
  const issues: string[] = [];
  const seen = new Set<string>();

  for (const repo of repos) {
    const key = repo.remote.toLowerCase();
    if (seen.has(key)) {
      issues.push(`"${repo.remote}" is declared twice; one step checks a repository out once`);
    }
    seen.add(key);

    if (repo.path !== undefined && !isSafeWorkspacePath(repo.path)) {
      issues.push(
        `"${repo.remote}" would be checked out at "${repo.path}", which is not a path a workspace will hold`,
      );
    }
    if (repo.path !== undefined && (repo.path === '.reef-git' || repo.path.startsWith('.reef-git/'))) {
      issues.push(`"${repo.remote}" would be checked out over ".reef-git/", where reef records the checkout`);
    }
  }

  if (repos.length > 1) {
    for (const repo of repos) {
      if (repo.path === undefined) {
        issues.push(
          `"${repo.remote}" has no path, and a repository at the workspace root would clear every other checkout when it is refreshed — give each repository its own directory`,
        );
      }
    }
  }

  const paths = repos.flatMap((repo) => (repo.path === undefined ? [] : [repo.path.replace(/\/+$/, '')]));
  for (const path of paths) {
    for (const other of paths) {
      if (path === other) continue;
      if (other.startsWith(`${path}/`)) {
        issues.push(`"${other}" is inside "${path}", so refreshing one checkout would clear the other`);
      }
    }
  }
  const distinct = new Set(paths);
  if (distinct.size !== paths.length) {
    issues.push('two repositories are checked out at the same path, so one would overwrite the other');
  }

  return [...new Set(issues)];
}

/**
 * Inheritance for `workspace`, and it **tightens** rather than unions.
 *
 * This is the decision in this feature that is easiest to get wrong, so the
 * reasoning is written out rather than asserted.
 *
 * ARCHITECTURE §2 splits inheritance in two: capabilities union down the tree
 * (a child inherits every mount its ancestors had and may add more), and
 * restrictions tighten down it (policy, tool allowlists, limits). The tempting
 * reading of `repos` is "a repository is a thing you can reach, so it is a
 * capability, so it unions". That reading is wrong, and the way to see it is to
 * ask what a repo list is *narrowing*.
 *
 * It is narrowing the mounted git connection. A step reaches GitHub because it
 * mounts `git` — that mount is the capability, and it unions down exactly as
 * §2 says. `repos` says which repositories, out of everything that mount could
 * reach, this step's disk is prepared with. That makes it the same kind of
 * field as `ConnectionRef.tools`, which narrows which of a connection's tools a
 * step will use, and which `resolveStep` intersects rather than unions.
 *
 * Union is the escalation the brief warns about, concretely: a level-1 step
 * declares `acme/docs`, a reviewer reads the workflow and understands the blast
 * radius to be the docs repository, and a level-3 step three screens further
 * down adds `acme/payments`. Nothing in the parent said payments, and the
 * parent is where anybody looks. Under intersection the ancestor's list is the
 * ceiling for the whole subtree, and widening it is an edit to the ancestor —
 * in a diff, where a reviewer sees it. That is §2's stated trade, taken
 * deliberately: someone will want a child with a repository its parent lacks,
 * and the answer is to loosen the parent.
 *
 * An ancestor that declared no workspace has restricted nothing, so a
 * descendant declaring one is specifying rather than contradicting — the same
 * shape as `ConnectionRef.actor`, and the common case, since the workflow
 * usually says nothing and one step in the middle does the checkout.
 *
 * **A repository outside the ancestor's set throws** rather than intersecting
 * away to nothing. An empty tool allowlist is a coherent state; an empty repo
 * set on a step that asked for checkouts is a step standing on nothing, which
 * fails later and further away as "no checkout of acme/payments". Naming it
 * here names it where it can be fixed.
 *
 * `branch` and `path` follow `actor`'s rule for the same reason: an ancestor
 * that pinned one made a promise its prompt is written against ("the app is in
 * `web/`", "fix the failing test on `release`"), and a descendant silently
 * re-pointing it makes those instructions apply to code the ancestor never saw.
 * An ancestor that left it unset promised nothing, so a descendant may choose.
 */
export function mergeWorkspace(
  inherited: ResolvedWorkspace | null,
  own: WorkspaceSpec | undefined,
): ResolvedWorkspace | null {
  if (!own) return inherited;
  const declared: ResolvedWorkspace = {
    repos: own.repos.map((repo) => ({ ...repo })),
    environment: own.environment ?? null,
  };
  if (!inherited) return declared;

  if (
    inherited.environment &&
    declared.environment &&
    inherited.environment !== declared.environment
  ) {
    throw new Error(
      `step cannot change the workspace environment inherited from an ancestor from "${inherited.environment}" to "${declared.environment}"`,
    );
  }

  const environment = inherited.environment ?? declared.environment;

  // An ancestor with an environment and no explicit repos has still fixed the
  // set — it is the environment's set, resolved from D1 at step start — so
  // "the ancestor named no repositories" has to mean an empty `repos` *and* no
  // environment, or a descendant could name a repository the environment does
  // not contain and reach it by having got in first.
  if (inherited.repos.length === 0) {
    return { repos: declared.repos, environment };
  }
  if (declared.repos.length === 0) {
    return { repos: inherited.repos, environment };
  }

  const byRemote = new Map(inherited.repos.map((repo) => [repo.remote.toLowerCase(), repo]));
  const repos: WorkspaceRepoRef[] = [];

  for (const repo of declared.repos) {
    const ancestor = byRemote.get(repo.remote.toLowerCase());
    if (!ancestor) {
      throw new Error(
        `step declares repository "${repo.remote}", which is not in the set an ancestor step already fixed (${inherited.repos.map((entry) => entry.remote).join(', ')})`,
      );
    }
    if (ancestor.branch && repo.branch && ancestor.branch !== repo.branch) {
      throw new Error(
        `step cannot change the branch of inherited repository "${repo.remote}" from "${ancestor.branch}" to "${repo.branch}"`,
      );
    }
    if (ancestor.path !== undefined && repo.path !== undefined && ancestor.path !== repo.path) {
      throw new Error(
        `step cannot move inherited repository "${repo.remote}" from "${ancestor.path}" to "${repo.path}"`,
      );
    }
    repos.push({
      remote: ancestor.remote,
      ...((ancestor.branch ?? repo.branch) === undefined ? {} : { branch: ancestor.branch ?? repo.branch }),
      ...((ancestor.path ?? repo.path) === undefined ? {} : { path: ancestor.path ?? repo.path }),
    });
  }

  return { repos, environment };
}

/** Multi-repo is nothing more than this, and there is deliberately no second fan-out behind it. */
export function isMultiRepo(workspace: ResolvedWorkspace | null): boolean {
  return (workspace?.repos.length ?? 0) > 1;
}
